<script>
import APagination from './components/APagination.vue'
import AProduct from './components/AProduct.vue'
import AInput from './components/AInput.vue'
import DataEvent from './components/DataEvent.vue'
import AButton from './components/AButton.vue'
import { useSearchStore } from '@/stores/search'
import { useUIStore } from '@/stores/ui'
import HistoryStorage from './store/HistoryStorage'
import LoadingSpinner from './components/LoadingSpinner.vue'
import { debug } from './utils'
import { generateVisitorId, setVisitorIdCookie, getVisitorIdCookie } from './visitorIdUtils';

const orderOptions = [
  {
    label: 'order.relevance',
    code: '',
  },
  {
    label: 'order.priceAsc',
    code: 'price',
  },
  {
    label: 'order.priceDesc',
    code: 'price desc',
  },
  {
    label: 'order.title',
    code: 'title',
  },
]

const formatBound = (int, bound) => {
  if (int[bound] && int[int[bound]] !== '*') {
    const inclusivity = int[bound].startsWith('exclusive') ? 'e' : 'i'
    return `${int[int[bound]]}${inclusivity}`
  } else {
    return '*'
  }
}

const formatIntervalFilter = (key, int) => {
  return `(${key}: IN(${formatBound(int, 'min')},${formatBound(int, 'max')}))`
}

export default {
  components: {
    APagination,
    AProduct,
    AInput,
    LoadingSpinner,
    DataEvent,
  },
  data() {
    const store = useSearchStore()
    const ui = useUIStore()

    return {
      store,
      ui,
      loading: false,
      results: null,
      orderBy: '',
      categories: {},
      facets: [],
      orderOptions,
      totalSize: 0,
      displayPath: [],
      showOverlay: false,
      metaFilter: {},
      sharedData: '',
      visitorId: null,
    }
  },
  computed: {
    localeSuffix() {
      return this.store.locale === 'en' ? '' : `_${this.store.locale}`
    },
    currentLevel() {
      return this.displayPath.length
    },
    applicableFacets() {
      return this.facets.filter((f) => f?.values.length && !f.key.startsWith('attributes.cat_level_'))
    },
    filterQuery() {
      const filters = []

      if (this.store.path.length) {
        this.store.path.forEach((cat, i) => {
          filters.push(`(attributes.cat_level_${i}${this.localeSuffix}: ANY("${cat}"))`)
        })
      }

      const multiFilters = Object.entries(this.store.filters)
        .filter(([, v]) => v.length)
        .map(([key, values]) => {
          if (Object.hasOwn(values[0], 'min') || Object.hasOwn(values[0], 'max')) {
            return `(${values.map((int) => formatIntervalFilter(key, int)).join(' OR ')})`
          } else {
            return `(${key}: ANY(${values.map((v) => `"${v}"`).join(',')}))`
          }
        })

      return [...filters, ...multiFilters].join(' AND ')
    },
    isFiltered() {
      return !!this.filterQuery
    },
    root() {
      return this.$el.getRootNode().host || this.$el
    },
  },
  created() {
    const existingVisitorId = getVisitorIdCookie();
    if (!existingVisitorId) {
      const visitorId = generateVisitorId();
      setVisitorIdCookie(visitorId, 365);
    }
    this.visitorId = existingVisitorId;

    window.onpopstate = async (event) => {
      if (!event.state) {
        location.reload()
      } else {
        debug('Popping state', event.state)
        HistoryStorage.popping = true
        this.store.$patch(event.state)
        HistoryStorage.popping = false
        await this.search({ resetPagination: false })
      }
    }
  },
  async mounted() {
    debug('Mounted')
    if (this.store.query || this.isFiltered) {
      await this.search({ resetPagination: false })
    }

    this.root.addEventListener('ap:search', (e) => {
      const { query, params } = e.detail

      this.newQuery(query, params)
    })
  },
  methods: {
    async applyCustomPriceFilter() {
      const facet = `price`
      let { min, max } = this.store.customPriceFilter

      min = parseInt(min) || '*'
      max = parseInt(max) || '*'

      if (parseInt(max) < parseInt(min)) {
        max = '*'
      }

      this.store.filters[facet] = [
        {
          exclusiveMaximum: max,
          max: 'exclusiveMaximum',
          min: 'minimum',
          minimum: min,
        },
      ]

      await this.search()
    },
    resolveGateway() {
      const data = location.pathname.split('/').filter(Boolean)?.[0] || ''
      const [, gateway, locale] = data.match(/^([A-Z]{3})_([a-z]{2})$/) || []
      debug('Resolved gateway: ', gateway, locale)
      gateway && Object.assign(this.store, { gateway, locale })
    },
    async newQuery(query, { visitorId } = {}) {
      debug('New query:', { query, visitorId })
      this.resetFilters()
      Object.assign(this.store, { query, visitorId })
      await this.search()
    },
    async reOrder() {
      await this.search()
    },
    async clearFilters() {
      this.resetFilters()
      this.resetAvailability()
      await this.search()
    },
    async clearFilter(facet) {
      this.store.filters[facet] = []
      if (facet.includes('price')) {
        this.store.resetCustomPriceFilter()
      }
      await this.search()
    },
    async clearCategory() {
      this.store.path = []
      await this.search()
    },
    async applyCategory(category, level) {
      this.store.path.splice(level, Infinity, category)
      await this.search()
    },
    async applyFilter(facet) {
      await this.search({
        preserveFilter: this.store.filters[facet].length ? facet : null,
      })
    },
    resetOrder() {
      this.store.orderBy = ''
    },
    resetFilters() {
      this.store.path = []
      this.store.filters = {}
      this.store.resetCustomPriceFilter()
      this.resetAvailability()
    },
    resetAvailability() {
      this.store.onlyInStock = true
    },
    async go(page, scrollIntoView = false) {
      this.store.page = page
      await this.search({
        resetPagination: false,
        scrollIntoView,
      })
    },

    async search({ preserveFilter = null, resetPagination = true, scrollIntoView = false } = {}) {
      this.showOverlay = false
      this.loading = true

      if (resetPagination) {
        this.store.page = 0
      }

      const params = {
        gateway: this.store.gateway,
        locale: this.store.locale,
        visitorId: this.visitorId,
        onlyInStock: this.store.onlyInStock || '',
        pageSize: this.store.pageSize,
        page: this.store.page,
        query: this.store.query,
        path: this.store.path,
        orderBy: this.store.orderBy,
        onlyInStock: this.store.onlyInStock || '',
        filter: encodeURIComponent(this.filterQuery),
      }

      debug('Performing search %O', params)

      const path = `/api/search?${new URLSearchParams(params).toString()}`

      const url = new URL(path, import.meta.env.VITE_API_HOST || `https://${import.meta.env.VITE_VERCEL_URL}`)

      const response = await fetch(url)
      this.results = await response.json()

      this.sharedData = {
        results: this.results,
        params: params,
      };

      this.totalSize = this.results.totalSize

      this.displayPath = [...this.store.path]

      this.handleFacets(this.results.facets, preserveFilter)

      scrollIntoView && this.$refs['results']?.scrollIntoView({ behavior: 'smooth' })

      this.loading = false
    },
    handleFacets(facets, preserveFilter) {
      this.categories = facets.find((f) => f.key === `attributes.cat_level_${this.currentLevel}${this.localeSuffix}`)

      if (preserveFilter) {
        const preservedValues = this.facets.find((f) => f.key === preserveFilter)?.values
        if (preservedValues) {
          facets.find((f) => f.key === preserveFilter).values = preservedValues
        }
      }

      this.facets = facets

      this.applicableFacets.forEach((f) => {
        f.title = f.key.replace('attributes.', '').replace(/^inventory\(\w*,(\w*)\)/, '$1')
        if (!(f.key in this.store.filters)) {
          this.store.filters[f.key] = []
        }

        this.metaFilter[f.key] =
          preserveFilter === f.key && f.key in this.metaFilter
            ? this.metaFilter[f.key]
            : {
                filter: '',
                showMore: false,
              }
      })
    },
    formatInterval(i, facet) {
      const prefix = facet.includes('price') ? '$' : ''
      if (i.min && i.max) {
        return `${prefix}${i[i.min]} - ${prefix}${i[i.max]}`
      } else if (i.min) {
        return `${prefix}${i[i.min]} ` + this.$t('price.andOver')
      } else if (i.max) {
        return this.$t('price.under') + ` ${prefix}${i[i.max]}`
      }
    },
    toggleCollapse(facet) {
      const index = this.ui.collapsed.indexOf(facet)
      if (~index) {
        this.ui.collapsed.splice(index, 1)
      } else {
        this.ui.collapsed.push(facet)
      }
    },
    filteredFacetValues(facet) {
      const filter = this.metaFilter[facet.key].filter

      if (filter) {
        const regex = new RegExp(filter, 'i')
        return facet.values.filter(({ value }) => ~value.search(regex))
      } else {
        return facet.values
      }
    },
  },
}
</script>

<template>
  <div class="w-full flex flex-col items-center">
    <LoadingSpinner class="max-w-md m-20" v-if="loading && !results" />
    <div class="w-full max-w-7xl flex flex-col sm:flex-row" v-if="results" :class="{ 'opacity-50': loading }">
      <div class="w-full sm:w-64 p-4 bg-white overlay" :class="{ show: showOverlay }">
        <span class="inline sm:hidden float-right hover:underline cursor-pointer text-3xl font-bold p-2 leading-4	" @click="showOverlay = false">&times;</span>
        <div class="mb-4">
          <h2 class="text-xl font-semibold">{{ $t('orderBy') }}</h2>
          <select v-model="store.orderBy" @change="reOrder()" class="w-full rounded-full">
            <option v-for="option in orderOptions" :value="option.code" :key="option.code">
              {{ $t(option.label) }}
            </option>
          </select>
        </div>
        <span v-if="isFiltered" class="hidden sm:inline float-right hover:underline cursor-pointer" @click="clearFilters">&times; {{ $t('clearAll') }}</span>
        <h2 class="text-xl font-semibold mb-2">{{ $t('filters.title') }}</h2>
        <div class="mb-4" v-if="currentLevel || categories?.values.length">
          <h2 class="text-lg font-semibold">
            <span
              v-if="store.path?.length"
              class="float-right hover:underline cursor-pointer text-sm font-thin mr-5 mt-1"
              @click.stop="clearCategory()"
              >&times; {{ $t('clear') }}</span
            >
            {{ $t('category.title') }}
          </h2>
          <div v-for="(j, i) in currentLevel" :key="i" :class="`pl-${i*2}`">
            <a href="#" @click.prevent="applyCategory(displayPath[i], i)">
              <span :class="{ 'font-semibold': j === currentLevel }">{{ displayPath[i] }}</span>
            </a>
          </div>
          <template v-if="categories">
            <div v-for="(val, i) in categories.values" :key="i" :class="`pl-${currentLevel*2}`">
              <a href="#" @click.prevent="applyCategory(val.value, currentLevel)" class="flex">
                <span class="truncate">{{ val.value }}</span>
                <span>&nbsp;({{ val.count }})</span>
              </a>
            </div>
          </template>
        </div>
        <div class="mb-4">
          <h2 class="text-lg font-semibold capitalize">{{ $t('availability.title') }}</h2>
          <label>
            <input
              type="checkbox"
              class="mr-2"
              v-model="store.onlyInStock"
              @change="search({ resetPagination: true })"
            />
            <span>{{ $t('availability.inStock') }}</span>
          </label>
        </div>
        <div v-for="facet in applicableFacets" :key="facet.key" class="mb-4">
          <h2 class="text-lg font-semibold capitalize cursor-pointer" @click="toggleCollapse(facet.key)">
            <span
              class="float-right font-bold text-lg mr-1 transition-transform"
              :class="{ 'rotate-180': !ui.collapsed.includes(facet.key) }"
            >
              <i
                class="border-gray-600 border-t-0 border-r-4 border-b-4 border-l-0 inline-block p-1 scale-75 rotate-45"
              ></i>
            </span>
            <span
              v-if="store.filters[facet.key]?.length"
              class="float-right hover:underline cursor-pointer text-sm font-thin mr-5 mt-1"
              @click.stop="clearFilter(facet.key)"
              >&times; {{ $t('clear') }}</span
            >
            {{ $t(`facets.${facet.title}`) }}
            <span class="font-normal" v-if="ui.collapsed.includes(facet.key)">({{ facet.values.length }})</span>
          </h2>
          <div class="transition-max-height duration-300 overflow-y-hidden clear-both" :class="{'max-h-0': ui.collapsed.includes(facet.key), 'max-h-infinity': !ui.collapsed.includes(facet.key)}">
            <AInput v-if="facet.values.length > 15" v-model="metaFilter[facet.key].filter" />
            <div v-if="facet.key.includes('price')" class="flex gap-1 justify-between mb-4 mt-2">
              <AInput
                type="number"
                min="0"
                step="10"
                no-margins
                v-model.number="store.customPriceFilter.min"
                :placeholder="$t('checkprice.min')"
                class="rounded"
                @keyup.enter="applyCustomPriceFilter"
              />
              <label class="leading-8"> - </label>
              <AInput
                type="number"
                :min="store.customPriceFilter.min || 0"
                step="10"
                no-margins
                v-model.number="store.customPriceFilter.max"
                :placeholder="$t('checkprice.max')"
                class="rounded"
                @keyup.enter="applyCustomPriceFilter"
              />
              <button @click="applyCustomPriceFilter" class="bg-brand text-white h-8 px-4 rounded">{{ $t('checkprice.apply') }}</button>
            </div>
            <div class="transition-max-height max-h-infinity" :class="{'fadeout max-h-96': filteredFacetValues(facet).length > 15 && !metaFilter[facet.key].showMore}">
              <div v-for="(val, i) in filteredFacetValues(facet)" :key="i">
                <label v-if="val.facetValue === 'value'" class="cursor-pointer flex items-center">
                  <input
                    type="checkbox"
                    class="mr-2"
                    v-model="store.filters[facet.key]"
                    :value="val.value"
                    @change="applyFilter(facet.key)"
                  />
                  <span class="truncate">{{ val.label || val.value }}</span><span>&nbsp;({{ val.count }})</span>
                </label>
                <label v-else-if="val.facetValue === 'interval'" class="cursor-pointer flex">
                  <input
                    type="checkbox"
                    class="mr-2"
                    v-model="store.filters[facet.key]"
                    :value="val.interval"
                    @change="applyFilter(facet.key)"
                  />
                  <span>{{ formatInterval(val.interval, facet.key) }} ({{ val.count }})</span>
                </label>
              </div>
            </div>
            <div v-if="filteredFacetValues(facet).length > 15" class="text-center">
              <span class="cursor-pointer" v-if="metaFilter[facet.key].showMore" @click="metaFilter[facet.key].showMore = false">{{ $t('showLess') }}</span>
              <span class="cursor-pointer" v-else @click="metaFilter[facet.key].showMore = true">{{ $t('showMore') }}</span>
            </div>
          </div>
        </div>
      </div>
      <div ref="results" class="flex-1 px-2">
        <div class="bg-brand-light/50 text-brand mb-2 mt-2 p-4 rounded flex flex-col sm:flex-row items-center">
          <p class="flex-1">
            <span class="font-semibold">{{ results.totalSize }}</span> {{ $t('results.results') }}
            <template v-if="store.query">
              {{ $t('results.for') }} '<span class="font-semibold">{{ results.correctedQuery || store.query }}</span>'.
              <span v-if="results.correctedQuery">({{ $t('results.correctedFrom') }} '{{ store.query }}') </span>
            </template>
          </p>
          <APagination @go="go" :totalSize="results.totalSize" />
        </div>

        <div class="sm:hidden flex justify-center mb-4">
          <button @click="showOverlay = true" class="w-full text-brand bg-brand-compliment border border-brand-compliment rounded p-2 text-lg leading-4 px-4">{{ $t('results.filterResults') }}</button>
          <button v-if="isFiltered" @click="clearFilters" class="ml-2 text-brand active:bg-brand active:text-white border border-brand rounded px-4">{{ $t('clear') }}</button>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1 sm:gap-2">
          <AProduct v-for="({ product }, i) in results.results" :key="i" v-bind="{ product, loading }" />
        </div>

        <div class="bg-brand-light/50 p-4 my-2 rounded flex flex-col sm:flex-row items-center">
          <p class="flex-1"></p>
          <APagination @go="go($event, true)" :totalSize="results.totalSize" />
        </div>
      </div>
    </div>
  </div>
  <DataEvent :searchEventData="sharedData" />
</template>

<style>
@import url("./assets/main.css");
</style>

